import React from 'react';
import { FileText, Image as ImageIcon, Upload, X } from 'lucide-react';
import CommunityPillGroup from './CommunityPillGroup';
import {
  COMMUNITY_POST_TYPES,
  COMMUNITY_PILLARS,
} from '../../utils/communityConstants';

const TYPE_OPTIONS = COMMUNITY_POST_TYPES.map((t) => ({ value: t.id, label: t.label }));
const PILLAR_OPTIONS = COMMUNITY_PILLARS.map((p) => ({ value: p.id, label: p.label }));

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentPreview = ({ file, onRemove }) => {
  const isImage = file.type?.startsWith('image/');
  const isPdf = file.type === 'application/pdf';
  const FileIcon = isImage ? ImageIcon : FileText;

  return (
    <div className="group relative rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block h-32 bg-gray-100 dark:bg-gray-900"
        title={`Open ${file.name}`}
      >
        {isImage && file.url ? (
          <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
        ) : isPdf && file.url ? (
          <iframe
            src={`${file.url}#toolbar=0&navpanes=0&view=FitH`}
            title={file.name}
            className="w-full h-full pointer-events-none"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-400">
            <FileText className="w-8 h-8" />
            <span className="text-xs uppercase tracking-wide">
              {file.name?.split('.').pop()}
            </span>
          </div>
        )}
      </a>
      <div className="flex items-center gap-1.5 px-2.5 py-2">
        <FileIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium truncate text-gray-900 dark:text-white">{file.name}</p>
          {file.size ? (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">{formatSize(file.size)}</p>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
        aria-label={`Remove ${file.name}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const CommunityNewPostModal = ({
  isOpen,
  onClose,
  newPost,
  setNewPost,
  onSubmit,
  onFileChange,
  onRemoveFile,
  uploadingFiles,
  isSubmittingPost,
}) => {
  if (!isOpen) return null;

  const inputClass =
    'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2.5 px-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

  const labelClass = 'block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300';

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl max-w-3xl w-full mx-auto flex flex-col max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-post-title"
      >
        <div
          className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-800"
        >
          <div className="flex-1" />
          <h3
            id="new-post-title"
            className="text-lg font-semibold flex-1 text-center text-gray-900 dark:text-white"
          >
            Create New Post
          </h3>
          <div className="flex-1 flex justify-end">
            <button type="button" onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <p className={labelClass}>Post type</p>
            <CommunityPillGroup
              options={TYPE_OPTIONS}
              value={newPost.type}
              onChange={(value) => setNewPost({ ...newPost, type: value })}
              ariaLabel="Post type"
            />
          </div>

          <div>
            <p className={labelClass}>Pillar</p>
            <CommunityPillGroup
              options={PILLAR_OPTIONS}
              value={newPost.category}
              onChange={(value) => setNewPost({ ...newPost, category: value })}
              ariaLabel="Pillar"
            />
          </div>

          <div>
            <label htmlFor="post-title" className={labelClass}>
              Title
            </label>
            <input
              type="text"
              id="post-title"
              value={newPost.title}
              onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
              className={inputClass}
              placeholder="Enter a descriptive title"
            />
          </div>

          <div>
            <label htmlFor="post-description" className={labelClass}>
              Description
            </label>
            <textarea
              id="post-description"
              value={newPost.description}
              onChange={(e) => setNewPost({ ...newPost, description: e.target.value })}
              rows="6"
              className={inputClass}
              placeholder="Share your success, issue, or idea in detail..."
            />
          </div>

          <div>
            <p className={labelClass}>Attachments</p>
            <div
              className="border-2 border-dashed rounded-xl p-4 border-gray-300 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40"
            >
              <div className="flex items-center justify-center">
                <label
                  className={`flex items-center justify-center space-x-2 cursor-pointer ${
                    uploadingFiles ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {uploadingFiles ? 'Uploading...' : 'Upload files or images'}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={onFileChange}
                    disabled={uploadingFiles}
                  />
                </label>
              </div>

              {newPost.files?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {newPost.files.map((file, index) => (
                    <AttachmentPreview
                      key={index}
                      file={file}
                      onRemove={() => onRemoveFile(file.name)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 flex justify-end gap-3"
        >
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={uploadingFiles || isSubmittingPost}
            className={`px-4 py-2 text-sm font-medium text-white rounded-xl shadow-sm transition-colors ${
              uploadingFiles || isSubmittingPost
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {uploadingFiles || isSubmittingPost ? 'Posting...' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunityNewPostModal;
